import { apiClient } from './client'

/**
 * Интерактивные задания темы: конструктор восемнадцати типов, выдача в класс,
 * попытки учеников с автопроверкой и очередь проверки учителем.
 *
 * Эталоны в `content` приходят ученику вычищенными, пока попытка активна:
 * иначе задание решается чтением ответа из сетевой панели браузера. Учителю
 * содержимое отдаётся целиком.
 */

export type ExerciseType =
  | 'drag_gaps'
  | 'cloze'
  | 'dropdown_gaps'
  | 'choice'
  | 'true_false'
  | 'matching'
  | 'sentence_builder'
  | 'ordering'
  | 'grouping'
  | 'error_correction'
  | 'translation'
  | 'picture'
  | 'crossword'
  | 'listening'
  | 'open_answer'
  | 'oral_answer'
  | 'dictation'
  | 'card_test'

export type ExerciseLevel = 'low' | 'medium' | 'high'

export type ExerciseStatus = 'draft' | 'ready' | 'archived'

export type ExerciseRunStatus = 'active' | 'teacher_review' | 'checked' | 'cancelled'

export type ExerciseRunContext = 'lesson' | 'homework' | 'self'

export type ExerciseRunMode = 'graded' | 'training'

/**
 * Типы под премиум-подпиской (premium_exercises_enabled).
 *
 * Список повторяет `ExerciseType::premium()` на сервере — расхождение здесь
 * означает либо замок там, где API разрешает, либо 403 на сохранении уже
 * заполненной формы.
 */
export const PREMIUM_EXERCISE_TYPES: ExerciseType[] = [
  'dictation',
  'listening',
  'open_answer',
  'crossword',
]

/** Типы, которые выполняются своим доменом: у них своя страница и свой прогон */
export const DELEGATE_EXERCISE_TYPES: ExerciseType[] = ['card_test']

export type ExerciseSettings = {
  max_attempts?: number | null
  show_correct_after?: 'never' | 'after_check' | 'after_attempts'
  shuffle?: boolean
  ignore_case?: boolean
}

export type Exercise = {
  id: number
  type: ExerciseType
  level: ExerciseLevel
  title: string
  instructions: string | null
  status: ExerciseStatus
  is_system: boolean
  /** Происхождение: bank либо legacy_assignment_copy */
  origin: string
  settings: ExerciseSettings | null
  /** Состав зависит от типа; ученику эталоны вычищены до сдачи */
  content: Record<string, unknown>
  content_updated_at: string | null
}

export type ExerciseDelegate = {
  kind: 'card_test'
  id: number
  status?: string
  score_percent?: number | null
}

export type ExerciseRun = {
  id: number
  exercise_id: number
  student_id: number
  context: ExerciseRunContext
  mode: ExerciseRunMode
  lesson_id: number | null
  homework_item_id: number | null
  attempt_number: number
  status: ExerciseRunStatus
  answers: Record<string, unknown> | null
  /** Прослушивания диктанта: считает сервер, клиентский счётчик сбрасывается перезагрузкой */
  plays_used?: number
  plays_left?: number | null
  auto_score_percent: number | null
  teacher_score_percent: number | null
  score_percent: number | null
  teacher_comment: string | null
  delegate?: ExerciseDelegate | null
  /** Слот под AI-черновик проверки: приходит только учителю и пока всегда null */
  ai_review?: Record<string, unknown> | null
  exercise_changed: boolean
  /** Версия задания на момент сдачи — приходит учителю, когда задание правили */
  content_snapshot?: Record<string, unknown> | null
  submitted_at: string | null
  checked_at: string | null
  /**
   * Версия работы для оптимистичной блокировки: её же возвращаем в
   * `expected_updated_at` при проверке. Без неё сервер не может отличить
   * осознанную переоценку от записи поверх чужого вердикта.
   */
  updated_at: string | null
  exercise?: Exercise
  student?: { id: number; name: string }
}

/**
 * Строка пикера домашки — задание плюс слот, из которого оно показано.
 *
 * `assigned_element_id` — id элемента темы назначения (строка
 * `assigned_theme_elements`), то есть место работы в программе ученика. Оно
 * есть только у строк секции `program`: сервер группирует её по элементам, а
 * не по заданиям, и одно задание банка, стоящее в двух темах, приходит двумя
 * строками — различает их именно элемент. В секции `bank` задание вне
 * программы ученика, слота у него не существует, поле приходит `null`.
 *
 * Без этого поля пункт домашки уезжал в статистику первой попавшейся темы:
 * сервер угадывал слот фолбэком.
 */
export interface HomeworkPickerExercise extends Exercise {
  assigned_element_id?: number | null
}

/** Тема назначения с её заданиями — группа в пикере домашки. */
export interface HomeworkExerciseGroup {
  assigned_theme_id: number
  title: string | null
  section_title: string | null
  /** Тема закреплена за этим уроком — такие идут первыми */
  pinned: boolean
  exercises: HomeworkPickerExercise[]
}

/**
 * Пикер заданий домашки: программа ученика и банк учителя в одном ответе.
 *
 * Две секции, а не один список: задание из программы попадает в домашку
 * слотом темы, а задание банка — само по себе. Банк приходит страницами и уже
 * без тех заданий, что показаны в программе, — иначе учитель видел бы одно и
 * то же задание дважды и не понимал, чем строки отличаются.
 */
export interface HomeworkExercisePicker {
  program: HomeworkExerciseGroup[]
  bank: {
    /** Слота у этих заданий нет: `assigned_element_id` здесь всегда `null` */
    items: HomeworkPickerExercise[]
    total: number
    page: number
    per_page: number
  }
}

/** Происхождение задания в банке: своё или витрина платформы */
export type ExerciseSource = 'own' | 'system'

/**
 * Места, откуда задание сняли при архивации.
 *
 * Приходит из `DELETE /exercises/{id}`: учителю важно узнать, что задание
 * стояло в чужой для него теме или в уже выданной домашке, — до архивации
 * этих чисел на клиенте нет.
 */
export interface ExerciseUsageSummary {
  /** Элементы тем в каталоге программ */
  catalog: number
  /** Живые блоки в назначенных ученикам темах */
  assignments: number
  /** Пункты уже выданных домашек */
  homework_items: number
}

/**
 * Смета озвучки текста: цена, лимит и остаток баланса учителя.
 *
 * Имена полей — как у сервиса свободной озвучки: `chars`, а не `characters`.
 */
export type TextTtsEstimate = {
  chars: number
  max_chars: number
  price: string
  currency: string
  balance: string
  /** Хватает ли баланса учителя на синтез */
  sufficient: boolean
  /** Фраза уже есть в общем кеше озвучки — списание по дисконтной цене */
  cached: boolean
}

export type StoreExercisePayload = {
  type?: ExerciseType
  title?: string
  level?: ExerciseLevel
  instructions?: string | null
  content?: Record<string, unknown>
  settings?: ExerciseSettings | null
  status?: ExerciseStatus
}

/** Страница банка заданий */
export interface ExerciseBankPage {
  items: Exercise[]
  total: number
}

export interface ExerciseBankFilters {
  type?: ExerciseType | ''
  level?: ExerciseLevel | ''
  status?: ExerciseStatus | ''
  /** Чьи задания показывать: пусто — свои и витрина вместе */
  source?: ExerciseSource | ''
  q?: string
  /** Номер страницы, 1-based — как в API (MUI-пагинация 0-based, её сдвигает вызывающий) */
  page?: number
  perPage?: number
}

export const exercisesApi = {
  /**
   * Банк заданий: свои и витрина платформы.
   *
   * Архив по умолчанию скрыт — это склад, а не рабочий список.
   */
  async listBank(filters: ExerciseBankFilters = {}): Promise<ExerciseBankPage> {
    const { data } = await apiClient.get<{ data: Exercise[]; meta?: { total?: number } }>('/exercises', {
      params: {
        ...(filters.type ? { type: filters.type } : {}),
        ...(filters.level ? { level: filters.level } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.source ? { source: filters.source } : {}),
        ...(filters.q ? { q: filters.q } : {}),
        page: filters.page ?? 1,
        per_page: filters.perPage ?? 20,
      },
    })

    return { items: data.data, total: data.meta?.total ?? data.data.length }
  },

  /** Поставить задание банка в тему программы */
  async attachToTheme(themeId: number, exerciseId: number): Promise<void> {
    await apiClient.post(`/themes/${themeId}/exercise-links`, { exercise_id: exerciseId })
  },

  async listByTheme(themeId: number): Promise<Exercise[]> {
    const { data } = await apiClient.get<{ data: Exercise[] }>(`/themes/${themeId}/exercises`)
    return data.data
  },

  /**
   * Задания темы урока — единственный вход для ученика: `exercises/{id}`
   * учительский, и по нему класс получал бы 403 на каждое задание.
   */
  async listByLesson(lessonId: number): Promise<Exercise[]> {
    const { data } = await apiClient.get<{ data: Exercise[] }>(`/lessons/${lessonId}/exercises`)
    return data.data
  },

  /**
   * Задания для пункта домашки: программа этого урока плюс банк учителя.
   *
   * Банк отдельной секцией и страницами: домашку задают не только по разобранной
   * теме, а искать задание в общем списке из сотен строк невозможно. Пересечение
   * секций отсеивает сервер — здесь чистить нечего.
   */
  async listForHomework(
    lessonId: number,
    params: { q?: string; page?: number; perPage?: number } = {},
  ): Promise<HomeworkExercisePicker> {
    const { data } = await apiClient.get<{ data: HomeworkExercisePicker }>(
      `/lessons/${lessonId}/homework-exercises`,
      {
        params: {
          ...(params.q ? { q: params.q } : {}),
          page: params.page ?? 1,
          per_page: params.perPage ?? 20,
        },
      },
    )
    return data.data
  },

  async create(themeId: number, payload: StoreExercisePayload): Promise<Exercise> {
    const { data } = await apiClient.post<{ data: Exercise }>(`/themes/${themeId}/exercises`, payload)
    return data.data
  },

  /**
   * Создать задание в банке, минуя программу.
   *
   * Учителю нужен склад заготовок: задание пригождается в домашке и в
   * самопрактике, а прежний единственный вход через тему программы заставлял
   * заводить тему ради одного упражнения.
   */
  async createInBank(payload: StoreExercisePayload & { type: ExerciseType }): Promise<Exercise> {
    const { data } = await apiClient.post<{ data: Exercise }>('/exercises', payload)
    return data.data
  },

  async get(id: number): Promise<Exercise> {
    const { data } = await apiClient.get<{ data: Exercise }>(`/exercises/${id}`)
    return data.data
  },

  async update(id: number, payload: StoreExercisePayload): Promise<Exercise> {
    const { data } = await apiClient.patch<{ data: Exercise }>(`/exercises/${id}`, payload)
    return data.data
  },

  /**
   * Архивировать задание. Возвращает, откуда его при этом сняли, — учителю
   * нужно понять последствия: задание могло стоять в уже выданной домашке.
   */
  async archive(id: number): Promise<ExerciseUsageSummary> {
    const { data } = await apiClient.delete<{ data?: { detached_from?: ExerciseUsageSummary } }>(`/exercises/${id}`)

    return data.data?.detached_from ?? { catalog: 0, assignments: 0, homework_items: 0 }
  },

  /** Копия задания себе: с темой — сразу в неё, без темы — в банк */
  async duplicate(id: number, themeId?: number): Promise<Exercise> {
    const { data } = await apiClient.post<{ data: Exercise }>(
      `/exercises/${id}/duplicate`,
      themeId ? { theme_id: themeId } : {},
    )
    return data.data
  },

  async uploadImage(file: File): Promise<{ path: string; url: string }> {
    const form = new FormData()
    form.append('image', file)
    const { data } = await apiClient.post<{ data: { path: string; url: string } }>('/exercises/images', form)
    return data.data
  },

  /**
   * Аудиофайл для задания «аудирование».
   *
   * Место расходуется из хранилища учителя: при нехватке сервер отвечает 422 и
   * файл на диск не ложится. Без этой загрузки тип listening был недоводим —
   * в форме было только поле «путь к файлу», которого учителю взять негде.
   */
  async uploadAudio(file: File): Promise<{ path: string; url: string }> {
    const form = new FormData()
    form.append('audio', file)
    const { data } = await apiClient.post<{ data: { path: string; url: string } }>('/exercises/audio-uploads', form)
    return data.data
  },

  /**
   * Смета озвучки до списания: синтез платный, и учитель должен увидеть цену
   * раньше, чем деньги уйдут с баланса.
   */
  async estimateTextTts(text: string): Promise<TextTtsEstimate> {
    const { data } = await apiClient.post<{ data: TextTtsEstimate }>('/tts/estimate-text', { text })
    return data.data
  },

  /**
   * Озвучить текст задания: списание с баланса учителя и запись `content.audio`.
   *
   * Отдельным вызовом, а не побочным эффектом сохранения: иначе каждая правка
   * формы стоила бы учителю денег. Озвучивается сохранённый текст задания.
   */
  async synthesize(id: number): Promise<Exercise> {
    const { data } = await apiClient.post<{ data: Exercise }>(`/exercises/${id}/synthesize`)
    return data.data
  },

  async uploadOralAnswer(runId: number, audio: Blob, filename = 'answer.webm'): Promise<{ path: string; url: string }> {
    const form = new FormData()
    form.append('run_id', String(runId))
    form.append('audio', audio, filename)
    const { data } = await apiClient.post<{ data: { path: string; url: string } }>('/exercises/oral-uploads', form)
    return data.data
  },
}

export const exerciseRunsApi = {
  /** Выдать задание всем участникам урока */
  async launch(lessonId: number, exerciseId: number): Promise<ExerciseRun[]> {
    const { data } = await apiClient.post<{ data: ExerciseRun[] }>(
      `/lessons/${lessonId}/exercises/${exerciseId}/launch`,
    )
    return data.data
  },

  async listByLesson(lessonId: number): Promise<ExerciseRun[]> {
    const { data } = await apiClient.get<{ data: ExerciseRun[] }>(`/lessons/${lessonId}/exercise-runs`)
    return data.data
  },

  /**
   * Попытка соединить пару в «соответствиях». Проверяет сервер: эталоны
   * ученику не отдаются, и подсветить верную пару на клиенте нечем.
   */
  async matchAttempt(
    runId: number,
    left: string,
    right: string,
  ): Promise<{ ok: boolean; matches: Record<string, string>; mistakes: number }> {
    const { data } = await apiClient.post<{ data: { ok: boolean; matches: Record<string, string>; mistakes: number } }>(
      `/exercise-runs/${runId}/match-attempt`,
      { left, right },
    )
    return data.data
  },

  async selfRun(exerciseId: number): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/exercises/${exerciseId}/self-run`)
    return data.data
  },

  async homeworkRun(itemId: number): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/homework-items/${itemId}/exercise-run`)
    return data.data
  },

  async get(runId: number): Promise<ExerciseRun> {
    const { data } = await apiClient.get<{ data: ExerciseRun }>(`/exercise-runs/${runId}`)
    return data.data
  },

  /**
   * Отметить прослушивание диктанта. Лимит ведёт сервер: клиентский счётчик
   * обнуляется перезагрузкой страницы, и запись слушали бы бесконечно.
   */
  async play(runId: number): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/exercise-runs/${runId}/play`)
    return data.data
  },

  async submit(runId: number, answers: Record<string, unknown>): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/exercise-runs/${runId}/submit`, { answers })
    return data.data
  },

  async cancel(runId: number): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/exercise-runs/${runId}/cancel`)
    return data.data
  },

  /**
   * Вердикт учителя: балл и/или комментарий.
   *
   * `scorePercent === null` — учитель сохраняет только комментарий: сервер в
   * этой ветке не трогает ни балл, ни статус и не шлёт ученику «задание
   * проверено». Раньше балл уходил всегда, и комментарий к автопроверенной
   * работе молча превращал балл робота в ручную оценку.
   *
   * `expectedUpdatedAt` — версия работы, по которой учитель рисовал экран
   * (`run.updated_at`). Разошлась с базой — сервер отвечает 409 вместо того,
   * чтобы затереть чужой вердикт.
   */
  async check(
    runId: number,
    scorePercent: number | null,
    comment?: string | null,
    expectedUpdatedAt?: string | null,
  ): Promise<ExerciseRun> {
    const { data } = await apiClient.post<{ data: ExerciseRun }>(`/exercise-runs/${runId}/check`, {
      score_percent: scorePercent,
      comment: comment ?? null,
      ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
    })
    return data.data
  },

}

/**
 * Конфликт оптимистичной блокировки проверки (HTTP 409).
 *
 * Отдельная проверка, а не разбор текста ошибки: сообщение сервера
 * локализовано и меняется, а учителю нужно не оно, а понятная развилка —
 * обновить работу и посмотреть чужой вердикт.
 */
export function isCheckConflictError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false

  return (error as { response?: { status?: number } }).response?.status === 409
}

/**
 * Срез очереди проверки.
 *
 * `pending` — работы, которые ждут учителя. `checked` — всё проверенное:
 * автопроверенные попытки, работы с ручной оценкой учителя и проверенные
 * текстовые блоки домашки. `all` — и то, и другое. Раньше `checked` отдавал
 * только автобаллы, а проверенный блок домашки не попадал ни в один срез —
 * учитель, проверив работу, терял её из виду насовсем.
 */
export type ReviewQueueState = 'pending' | 'checked' | 'all'

/** Источник работы: срез по нему — отдельный от `state` переключатель. */
export type ReviewQueueContext = 'lesson' | 'homework'

/** Строка единой очереди проверки: работа с урока, самопрактика или пункт домашки. */
export interface ReviewQueueRow {
  kind: 'exercise_run' | 'homework_item'
  /** id попытки или пункта — по kind */
  id: number
  student_id: number
  student_name: string | null
  title: string | null
  context: 'lesson' | 'homework' | 'self'
  lesson_id: number | null
  homework_id: number | null
  exercise_type: ExerciseType | null
  /**
   * Статус работы. `checked` — проверено роботом или учителем; кто именно,
   * видно по паре `auto_score_percent` / `score_percent`. У текстового блока
   * домашки статус читается по отметке проверки.
   */
  status: ExerciseRunStatus
  auto_score_percent: number | null
  /** Действующий балл: ручной, если учитель уже оценивал, иначе балл автопроверки */
  score_percent: number | null
  /** Когда работа сдана: у попытки submitted_at, у блока домашки completed_at */
  waiting_since: string | null
  /**
   * Версия работы. Возвращается в `expected_updated_at` при выставлении балла —
   * и для попытки (`exerciseRunsApi.check`), и для текстового блока домашки
   * (`checkHomeworkItem`). Без неё оценка ложится поверх чужого вердикта молча,
   * а ученик получает второе уведомление с другим процентом.
   */
  updated_at: string | null
}

/**
 * Фильтры очереди. Даты — `YYYY-MM-DD`, обе границы включительны и берут день
 * целиком: «с 1 по 5 марта» оставляет и то, что сдано 5-го поздно вечером.
 */
export interface ReviewQueueFilters {
  context?: ReviewQueueContext
  state?: ReviewQueueState
  /** Только этот ученик. Чужой id сервер отклоняет — 422, а не пустой список */
  studentId?: number | null
  submittedFrom?: string | null
  submittedTo?: string | null
  page?: number
  perPage?: number
}

export interface ReviewQueuePage {
  rows: ReviewQueueRow[]
  total: number
  /** Срез, который применил сервер, — он же значение по умолчанию */
  state: ReviewQueueState
  /** Фильтры, которые применил сервер: подписи рисуются по ответу */
  studentId: number | null
  submittedFrom: string | null
  submittedTo: string | null
}

export const reviewQueueApi = {
  /**
   * Очередь проверки. `state` по умолчанию не шлём: серверное значение —
   * `pending`, то есть прежний состав очереди. Пустые фильтры не шлём тоже —
   * пустая строка в дате для сервера не «нет фильтра», а неверная дата.
   *
   * Список учеников для фильтра берётся отдельным запросом
   * (`usersApi.listAllStudents`), а не из этого ответа: он не зависит от
   * страницы и среза, и повторять его в каждом ответе очереди незачем.
   */
  async list(params: ReviewQueueFilters = {}): Promise<ReviewQueuePage> {
    const perPage = params.perPage ?? 25
    const { data } = await apiClient.get<{
      data: ReviewQueueRow[]
      meta: {
        total: number
        state?: ReviewQueueState
        student_id?: number | null
        submitted_from?: string | null
        submitted_to?: string | null
      }
    }>('/review-queue', {
      params: {
        ...(params.context ? { context: params.context } : {}),
        ...(params.state ? { state: params.state } : {}),
        ...(params.studentId ? { student_id: params.studentId } : {}),
        ...(params.submittedFrom ? { submitted_from: params.submittedFrom } : {}),
        ...(params.submittedTo ? { submitted_to: params.submittedTo } : {}),
        page: params.page ?? 1,
        per_page: perPage,
      },
    })

    return {
      rows: data.data,
      total: data.meta.total,
      state: data.meta.state ?? 'pending',
      studentId: data.meta.student_id ?? null,
      submittedFrom: data.meta.submitted_from ?? null,
      submittedTo: data.meta.submitted_to ?? null,
    }
  },

  /**
   * Проверить текстовый пункт домашки. Пункт-задание проверяется через
   * `exerciseRunsApi.check`: его вердикт живёт в попытке.
   */
  async checkHomeworkItem(
    itemId: number,
    scorePercent: number,
    comment?: string | null,
    expectedUpdatedAt?: string | null,
  ): Promise<void> {
    await apiClient.patch(`/homework-items/${itemId}/check`, {
      score_percent: scorePercent,
      comment: comment ?? null,
      // Версия пункта из очереди: сервер отвечает 409, если работу успели
      // переоценить. Без неё защита была односторонней — агент перезаписать
      // вердикт учителя не мог, а учитель вердикт агента молча мог.
      expected_updated_at: expectedUpdatedAt ?? null,
    })
  },
}

export function isPremiumExerciseType(type: ExerciseType): boolean {
  return PREMIUM_EXERCISE_TYPES.includes(type)
}

export function isDelegateExerciseType(type: ExerciseType): boolean {
  return DELEGATE_EXERCISE_TYPES.includes(type)
}
